import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  ControlLabel,
  FormControl,
  HelpBlock,
  Button,
} from 'react-bootstrap';
import {
  Row,
  Col,
} from 'antd';

import i18n from '../../config/i18n';
import connectActionsAndReducers from '../../toolbox/connectActionsAndReducers';
import PatientInfo from '../../components/patient/PatientInfo';
import PatientData from '../../components/patient/PatientData';
import WoundList from '../../components/patient/WoundList';
import FPPContainer from '../fpp/FPPContainer';
import LoadingIcon from '../../components/shared/LoadingIcon';
import Link from '../../components/shared/Link';
import {
  WOUND_PDF,
  FPP_ASSESSMENT_DETAIL,
  FPP_ASSESSMENT_HISTORY,
} from '../../constants/exportPDFTypes';
import {
  DOCTOR_ID,
  ASSISTANT_ID,
  NURSE_ID,
} from '../../constants/RoleConstants';

const DISMISS_LAST_ALERT_TIMEOUT = 1000;
const FPPRoleWhiteList = [DOCTOR_ID, ASSISTANT_ID, NURSE_ID];
const styles = {
  loading: {
    textAlign: 'center',
  },
  modal: {
    hint: { textAlign: 'left' },
  },
};

export class PatientDetailContainer extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      fetchPatientDetail: PropTypes.func.isRequired,
      getAssessmentHistoryPDF: PropTypes.func.isRequired,
      getAssessmentPDF: PropTypes.func.isRequired,
      createDangerAlert: PropTypes.func.isRequired,
      createSuccessAlert: PropTypes.func.isRequired,
      logIn: PropTypes.func.isRequired,
      dismissLastAlert: PropTypes.func.isRequired,
    }).isRequired,
    patient: PropTypes.shape({
      isPatientListLoading: PropTypes.bool.isRequired,
      patientDetail: PropTypes.object.isRequired,
    }).isRequired,
    medicalRecordId: PropTypes.string.isRequired,
    router: PropTypes.object.isRequired,
    fpp: PropTypes.shape({
      assessments: PropTypes.arrayOf(PropTypes.object).isRequired,
    }).isRequired,
    user: PropTypes.shape({
      profile: PropTypes.shape({
        roleId: PropTypes.number,
      }),
    }),
    targetSection: PropTypes.string,
  };

  static defaultProps = {
    user: { profile: { roleId: null } },
    targetSection: '',
  };

  constructor(props) {
    super(props);
    this.onExportPDF = this.onExportPDF.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.state = {
      modal: false,
      account: '',
      password: '',
      pdfId: undefined,
      exportAssessmentId: undefined,
      exportAssessmentHistoryPatientId: undefined,
      exportPDFType: undefined,
    };
  }

  componentDidMount = async () => {
    const {
      actions: {
        fetchPatientDetail,
        createDangerAlert,
      },
      medicalRecordId,
    } = this.props;

    // Get patient detail
    const detailResult = await fetchPatientDetail(medicalRecordId);
    if (detailResult !== true) {
      createDangerAlert(detailResult.message);
    }
  };

  onExportPDF = (id, type) => {
    switch (type) {
      case WOUND_PDF:
        this.setState({
          modal: true,
          pdfId: id,
          exportPDFType: WOUND_PDF,
        });
        break;
      case FPP_ASSESSMENT_HISTORY:
        this.setState({
          modal: true,
          exportAssessmentHistoryPatientId: id,
          exportPDFType: FPP_ASSESSMENT_HISTORY,
        });
        break;
      case FPP_ASSESSMENT_DETAIL:
        this.setState({
          modal: true,
          exportAssessmentId: id,
          exportPDFType: FPP_ASSESSMENT_DETAIL,
        });
        break;
      default: break;
    }
  };

  onSubmit = async (e) => {
    await e.preventDefault();
    const {
      actions: {
        fetchExportPDF,
        getAssessmentHistoryPDF,
        getAssessmentPDF,
        createAlert,
        createSuccessAlert,
        dismissLastAlert,
        logIn,
      },
      medicalRecordId,
    } = this.props;
    const {
      pdfId,
      exportAssessmentId,
      exportPDFType,
    } = this.state;

    await logIn(
      this.account.value,
      this.password.value,
      {
        onSucceed: async () => {
          createSuccessAlert(i18n.t('validAuth'));

          // automatically dismiss the success alert
          setTimeout(() => {
            dismissLastAlert();
          }, DISMISS_LAST_ALERT_TIMEOUT);

          switch (exportPDFType) {
            case WOUND_PDF:
              await fetchExportPDF(pdfId);
              break;
            case FPP_ASSESSMENT_HISTORY:
              await getAssessmentHistoryPDF(parseInt(medicalRecordId, 10));
              break;
            case FPP_ASSESSMENT_DETAIL:
              await getAssessmentPDF(parseInt(exportAssessmentId, 10));
              break;
            default: break;
          }
        },
        onFail: err => createAlert({
          ...err,
          warningMsg: i18n.t('invalidAccountPwd'),
        }),
      }
    );

    this.setState({
      modal: false,
    });
  };

  onCancel = () => {
    this.setState({
      modal: false,
    });
  };

  getPhotoDownloadURLCallback = (url) => {
    const {
      actions: {
        getPhotoDownloadURL,
      },
    } = this.props;
    return getPhotoDownloadURL(url);
  };

  fetchPatientDetailCallback = () => {
    const {
      actions: {
        fetchPatientDetail,
      },
      medicalRecordId,
    } = this.props;
    return fetchPatientDetail(medicalRecordId);
  };

  render() {
    const { loading } = styles;
    const {
      router,
      medicalRecordId,
      actions,
      patient: {
        isPatientDetailLoading,
        patientDetail,
      },
      fpp,
      user: { profile: { roleId } },
      targetSection,
    } = this.props;
    const { modal } = this.state;
    return (isPatientDetailLoading || Object.keys(patientDetail).length === 0)
      ? (
        <div className="col-md-12">
          <h1>
            {i18n.t('patientDetail')}
          </h1>
          <p className="lead">
            {i18n.t('examEditPatientRecord')}
          </p>
          <hr />
          <div style={loading}>
            <LoadingIcon />
          </div>
        </div>
      )
      : (
        <div className="col-md-12">
          <h1>
            {i18n.t('patientDetail')}
          </h1>
          <p className="lead">
            {i18n.t('examEditPatientRecord')}
          </p>
          <hr />
          <PatientInfo
            getPhotoDownloadURLCallback={this.getPhotoDownloadURLCallback}
            fetchPatientDetailCallback={this.fetchPatientDetailCallback}
            {...this.props}
          />
          <PatientData {...this.props} />
          <WoundList
            {...this.props}
            onExportPDF={pdfId => this.onExportPDF(pdfId, WOUND_PDF)}
            targetSection={targetSection}
          />
          <FPPContainer
            router={router}
            actions={actions}
            patientUserId={patientDetail.userId}
            medicalRecordId={medicalRecordId}
            assessments={fpp.assessments}
            onExportPDF={this.onExportPDF}
            viewOnly={!FPPRoleWhiteList.includes(roleId)}
            targetSection={targetSection}
          />
          <Modal show={modal}>
            <ModalHeader>
              {i18n.t('plzEnterAccountPwd')}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={this.onSubmit}>
                <FieldGroup
                  id="formControlsText"
                  type="text"
                  label="Account"
                  placeholder={i18n.t('account')}
                  inputRef={(input) => { this.account = input; }}
                />
                <FieldGroup
                  id="formControlsPassword"
                  type="password"
                  label="Password"
                  placeholder={i18n.t('pwd')}
                  onChange={this.handleInputChange}
                  inputRef={(input) => { this.password = input; }}
                />
              </Form>
            </ModalBody>
            <ModalFooter>
              <Row>
                <Col
                  xs={{ span: 24 }}
                  sm={{ span: 11 }}
                  style={styles.modal.hint}
                >
                  {i18n.t('downloadHintDescription')}
                  <Link router={router} href="/frequently-asked-questions">
                    {i18n.t('downloadHintFAQ')}
                  </Link>
                </Col>
                <Col
                  xs={{ span: 24 }}
                  sm={{ span: 13 }}
                >
                  <Button
                    bsStyle="success"
                    onClick={this.onSubmit}
                  >
                    {i18n.t('submit')}
                  </Button>
                  <Button
                    bsStyle="danger"
                    onClick={this.onCancel}
                  >
                    {i18n.t('cancel')}
                  </Button>
                </Col>
              </Row>
            </ModalFooter>
          </Modal>
        </div>
      );
  }
}

const FieldGroup = ({ id, label, help, ...props }) => (
  <FormGroup controlId={id}>
    <ControlLabel>{label}</ControlLabel>
    <FormControl {...props} />
    {
      help && <HelpBlock>{help}</HelpBlock>
    }
  </FormGroup>
);

export default connectActionsAndReducers(PatientDetailContainer);



// WEBPACK FOOTER //
// ./src/containers/patient/PatientDetailContainer.js